import React from 'react';
import { InputGroup, Button, Tooltip } from '@blueprintjs/core';
import { FieldMappingTreeNode } from '../../../../../../utils/fieldMappingTreeBuilder';
import { ValidationResult } from '../../../../../../utils/fieldMappingValidator';
import { MappingMode } from '../../../../../../types/DataSyncItem';
import { ValidationType } from '../../../../../../types/FieldMapping';
import styles from './TreeFieldMappingsTable.module.sass';

export interface SourceFieldCellProps {
  node: FieldMappingTreeNode;
  fieldMappingMode: MappingMode;
  sourceFieldDisplayMap: Map<string, string>;
  sourceFieldValidations: Map<string, ValidationResult>;
  showRequiredHint: boolean;
  onOpenDialog: (path: string) => void;
  onRemoveMapping: (path: string) => void;
}

/**
 * Source field cell component for TreeFieldMappingsTable
 * Handles rendering of source field selection, validation states, and mapping actions
 */
export const SourceFieldCell: React.FC<SourceFieldCellProps> = ({
  node,
  fieldMappingMode,
  sourceFieldDisplayMap,
  sourceFieldValidations,
  showRequiredHint,
  onOpenDialog,
  onRemoveMapping,
}) => {
  // Only show source field for leaf nodes
  if (!node.isLeaf) {
    return null;
  }

  // Determine if this field is editable
  // FIXED mode: only required fields are editable
  // EDITABLE mode: all fields are editable
  const isEditable =
    fieldMappingMode === MappingMode.EDITABLE ||
    (fieldMappingMode === MappingMode.FIXED && node.required);

  // Check for validation errors or warnings
  const validation = sourceFieldValidations.get(node.path);
  const hasValidation = !!validation;
  const isError = validation?.type === ValidationType.ERROR;
  const isWarning = validation?.type === ValidationType.WARNING;

  const displayValue = node.sourceField
    ? `${sourceFieldDisplayMap.get(node.sourceField) || node.sourceField}`
    : '';

  // Check if this is an unmapped required field
  const isUnmappedRequired = node.required && !node.sourceField;

  const sourceFieldCell = (
    <InputGroup
      className={`${styles.sourceFieldInput} ${
        isEditable
          ? styles.sourceFieldClickable
          : styles.sourceFieldDisabled
      } ${isError ? styles.sourceFieldError : ''} ${
        isWarning ? styles.sourceFieldWarning : ''
      } ${
        isUnmappedRequired && showRequiredHint
          ? styles.sourceFieldRequired
          : ''
      }`}
      value={displayValue}
      placeholder={
        node.sourceField ? undefined : 'Select source field...'
      }
      readOnly
      onClick={() => isEditable && onOpenDialog(node.path)}
    />
  );

  // Wrap sourceFieldCell in tooltip if there's a validation error or warning
  const sourceFieldWithTooltip = hasValidation ? (
    <Tooltip
      content={validation.message}
      placement="top"
      compact={true}
      minimal={true}
      hoverOpenDelay={200}
    >
      {sourceFieldCell}
    </Tooltip>
  ) : (
    sourceFieldCell
  );

  // Only show unlink button if mapping can be updated
  // In FIXED mode, fields cannot have their mappings updated except for required fields
  const canRemoveMapping =
    fieldMappingMode !== MappingMode.FIXED || node.required;

  return (
    <div className={styles.sourceFieldCellContainer}>
      {sourceFieldWithTooltip}
      {node.sourceField && canRemoveMapping && (
        <Tooltip
          content="Remove mapping"
          placement="top"
          compact
          minimal
          hoverOpenDelay={300}
        >
          <Button
            minimal
            small
            icon="unlink"
            className={styles.unlinkButton}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveMapping(node.path);
            }}
          />
        </Tooltip>
      )}
      {!node.sourceField && isUnmappedRequired && (
        <span
          className={styles.required}
          title="This required field must be mapped."
        >
          *
        </span>
      )}
    </div>
  );
};
